// @givmo/app-charity — email/password sign-in (/sign-in), the unified door's returning-email form.
//
// A controlled email/password form + a "remember me" toggle + a "Forgot password?" affordance (sends
// the reset email inline). On success the auth listener re-runs the guard; we navigate to the intended
// target (`redirect` search param) or the default landing. Validation uses the shared Zod schema; the
// submit button is disabled until valid (React re-renders synchronously — no Flutter paint-race gate).
//
// This is where /login-home's "Continue with Email" lands (the door's route-informed default — see
// auth-door.tsx). A caller without an account is one click from the create form: the footer link goes
// STRAIGHT to /sign-up/email (they already chose the email path; bouncing them back through a chooser
// would be a loop) and carries anything typed via the email handoff. That link matters more than it
// looks: `auth/invalid-credential` deliberately cannot distinguish "wrong password" from "no such
// account" (enumeration protection), so the footer is the no-account caller's only honest way out.
//
// A BOARD DIRECTOR ARRIVES HERE TOO (CBG-SEC2 SEC2-WEB, contract §4.3; mockup `m2-email-sign-in`):
// the director door's "Continue with email". One screen, an `arrival` prop rather than a copy, so the
// passwordless switch (AS-PWL-10) changes both in one place. The director arrival renders in Board's
// director shell, lands on the director's boards when there is no `redirect`, and its footer goes to
// the director's create form and back to the door. The console arrival is unchanged.

import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { Button, Card, CardBody, Checkbox, TextField, testIdProp } from '@givmo/design-system';
import {
  SignInDoorError,
  organizationSsoLabel,
  useOrganizationSsoAvailability,
} from '@givmo/passkeys';
import { DirectorShell } from '@givmo/board';
import { secondFactorChallengeOf, type TotpChallenge } from '@givmo/auth';
import type { UserCredential } from 'firebase/auth';
import { useAuth } from '../../auth/auth-context';
import { AuthShell } from './auth-shell';
import { TotpChallengePrompt } from './totp-challenge';
import { useAuthError } from './use-auth-error';
import { AUTH_ROUTES, DEFAULT_AUTHENTICATED_ROUTE } from './auth-routes';
import { signInSchema, forgotPasswordSchema } from './auth-schemas';
import { stashEmailHandoff, takeEmailHandoff } from './email-handoff';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { SIGN_IN_LANDING_STATE } from '../../console/sidebar-frame';
import { PRODUCT_NAME } from '../../lib/funnel-copy';
import { carriedDirectorLockNoticeLine } from '../board/director-lock-notice-line';
import { DIRECTOR_ROUTES } from '../board/director-routes';
import {
  DirectorAuthFooter,
  DIRECTOR_AUTH_LINK,
  DIRECTOR_AUTH_QUIET_LINK,
  directorEmailSearch,
} from './director-arrival';

export interface SignInScreenProps {
  /** Who arrived: the console's own sign-in (the default, unchanged) or a board director. */
  arrival?: 'console' | 'director';
}

export function SignInScreen({ arrival = 'console' }: SignInScreenProps = {}) {
  const director = arrival === 'director';
  const { signInWithEmail, sendPasswordReset } = useAuth();
  const navigate = useNavigate();
  // Two routes render this screen, so the search read is non-strict by necessity.
  const { redirect, arrival: directorArrival } = useSearch({ strict: false }) as {
    redirect?: string;
    arrival?: 'another_account';
  };
  const { error, lockRefused, clearError, surface } = useAuthError();
  // The way back in for someone their company locks out (AS-WEB26/27), returning to this form: in
  // Board's register for a director (cut 1007e's fold, Board's B-F3).
  const here = useLocation({ select: (location) => location.href });
  const openEscapeHatch = useCallback(
    () =>
      void navigate({
        to: director ? DIRECTOR_ROUTES.lostCompanyAccess : AUTH_ROUTES.lostCompanyAccess,
        search: { back: here },
      }),
    [navigate, here, director],
  );
  // Board's one line for the lock notice, carried from the director card's "Continue with email"
  // (an invitation's; Board's B-F2). Read once: the card wrote it before opening this form.
  const [lockNoticeLine] = useState(() => (director ? carriedDirectorLockNoticeLine() : undefined));
  // AS-WEB30: the lock notice offers the organization door first, where it can work here (frame
  // B4's notice); its step lands where this form would, and returns here. Never from Board's "use
  // another Givmo account" steps, where it would lead back to the account just left (Board's B-F4).
  const ssoAvailable =
    useOrganizationSsoAvailability() && !(director && directorArrival === 'another_account');
  const organizationSso = ssoAvailable
    ? {
        label: organizationSsoLabel('continue'),
        onOpen: () =>
          void navigate(
            director
              ? { to: DIRECTOR_ROUTES.organizationSso, search: redirect ? { redirect } : {} }
              : {
                  to: AUTH_ROUTES.organizationSso,
                  search: { ...(redirect ? { redirect } : {}), back: here },
                },
          ),
      }
    : undefined;

  // Prefilled when the caller crossed over from the create form with an address already typed.
  const [email, setEmail] = useState(takeEmailHandoff);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resetNotice, setResetNotice] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<TotpChallenge<UserCredential> | null>(null);

  const isValid = useMemo(
    () => signInSchema.safeParse({ email, password, rememberMe }).success,
    [email, password, rememberMe],
  );

  const goToDestination = useCallback(
    () =>
      navigate({
        to:
          sanitizeRedirectTarget(redirect) ??
          (director ? DIRECTOR_ROUTES.home : DEFAULT_AUTHENTICATED_ROUTE),
        state: SIGN_IN_LANDING_STATE,
      }),
    [navigate, redirect, director],
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isValid || submitting) return;
      clearError();
      setResetNotice(null);
      setSubmitting(true);
      try {
        await signInWithEmail({ email, password, rememberMe });
        await goToDestination();
      } catch (thrown) {
        // The password was accepted and a second factor is now owed (AS-WEB1) — a state of this
        // sign-in, not a failure of it.
        const pending = secondFactorChallengeOf<UserCredential>(thrown);
        if (pending !== null) {
          setChallenge(pending);
        } else {
          surface(thrown);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      isValid,
      submitting,
      clearError,
      signInWithEmail,
      email,
      password,
      rememberMe,
      goToDestination,
      surface,
    ],
  );

  const onForgotPassword = useCallback(async () => {
    clearError();
    setResetNotice(null);
    if (!forgotPasswordSchema.safeParse({ email }).success) {
      surface(new Error('Enter your email above first, then tap Forgot password.'));
      return;
    }
    try {
      await sendPasswordReset(email);
      setResetNotice('If an account exists for that email, a password-reset link is on its way.');
    } catch (thrown) {
      surface(thrown);
    }
  }, [clearError, email, sendPasswordReset, surface]);

  const prompt =
    challenge === null ? null : (
      <TotpChallengePrompt
        challenge={challenge}
        onResolved={goToDestination}
        onCancel={() => setChallenge(null)}
        onUnlink={openEscapeHatch}
        organizationSso={organizationSso}
        lockNoticeLine={lockNoticeLine}
      />
    );

  // The create form, for a caller with no account; the typed address rides the handoff. A director
  // takes Board's arrival across too, so a refusal back on this form still offers no organization
  // door behind Board's "use another Givmo account" steps (Board's R1).
  const goToCreate = () => {
    stashEmailHandoff(email);
    void navigate(
      director
        ? {
            to: DIRECTOR_ROUTES.signUpEmail,
            search: directorEmailSearch(redirect, directorArrival),
          }
        : { to: AUTH_ROUTES.signUpEmail, search: redirect ? { redirect } : {} },
    );
  };

  const form = (
    <form className="flex flex-col gap-16" onSubmit={(e) => void onSubmit(e)} noValidate>
      <TextField
        label="Email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        testId="auth-email-input"
      />
      <TextField
        label="Password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        testId="auth-password-input"
      />

      <div className="flex items-center justify-between gap-12">
        {/* The design-system control, not a bare `<input type="checkbox">`: the native box renders
              the platform's own unchecked border, which is the 1.48:1 boundary the SaaS Checkbox was
              retoned to clear. A funnel screen must not opt out of the fix. */}
        <label className="flex cursor-pointer items-center gap-8 font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
          <Checkbox
            appearance="saas"
            checked={rememberMe}
            onCheckedChange={setRememberMe}
            testId="auth-remember-me"
          />
          Remember me
        </label>
        <button
          type="button"
          onClick={() => void onForgotPassword()}
          className="font-sans text-s13 font-w600 tracking-givmo text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline motion-reduce:transition-none"
          {...testIdProp('auth-forgot-password')}
        >
          Forgot password?
        </button>
      </div>

      {/* B1: the reset send is an async outcome with no other visual change on the screen, so it is
            announced rather than only drawn. */}
      {resetNotice !== null && (
        <p
          role="status"
          className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
          {...testIdProp('auth-reset-notice')}
        >
          {resetNotice}
        </p>
      )}
      {/* AS-WEB27: the company lock's refusal is the notice with the way back in (frame B4's
          place: the form's error spot, above Sign in); anything else the red line. */}
      <SignInDoorError
        error={error}
        lockRefused={lockRefused}
        onUnlink={openEscapeHatch}
        organizationSso={organizationSso}
        line={lockNoticeLine}
      />

      <Button
        type="submit"
        appearance="saas"
        tone="primary"
        size="lg"
        fullWidth
        disabled={!isValid}
        loading={submitting}
        testId="auth-signin-submit"
      >
        Sign in
      </Button>
    </form>
  );

  if (director) {
    // `m2-email-sign-in`: the director's register, the form in a card, and the way back to the door.
    return (
      <DirectorShell title="Sign in with email" testId="page-director-email-sign-in">
        <Card className="w-full max-w-[440px]" testId="director-email-sign-in">
          <CardBody className="flex flex-col gap-20">
            {prompt ?? (
              <>
                {form}
                <DirectorAuthFooter>
                  <span>
                    New to Givmo?{' '}
                    <button
                      type="button"
                      onClick={goToCreate}
                      className={DIRECTOR_AUTH_LINK}
                      {...testIdProp('auth-get-started')}
                    >
                      Create an account
                    </button>
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      void navigate({
                        to: DIRECTOR_ROUTES.signIn,
                        search: redirect ? { redirect } : {},
                      })
                    }
                    className={DIRECTOR_AUTH_QUIET_LINK}
                    {...testIdProp('director-auth-back')}
                  >
                    Back to all options
                  </button>
                </DirectorAuthFooter>
              </>
            )}
          </CardBody>
        </Card>
      </DirectorShell>
    );
  }

  if (prompt !== null) {
    return <AuthShell testId="page-sign-in">{prompt}</AuthShell>;
  }

  return (
    <AuthShell testId="page-sign-in">
      <div className="flex flex-col gap-4">
        <h1 className="font-sans text-s22 font-w600 tracking-givmo text-text-strong">
          Sign in with email
        </h1>
        <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          Enter your email and password to continue.
        </p>
      </div>

      {form}

      {/* Straight to the create FORM, not back through a chooser — this caller already chose the
          email path. The typed address rides the handoff so recovery costs one click, not a retype. */}
      <p className="border-t border-border-subtle pt-20 text-center font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
        New to {PRODUCT_NAME}?{' '}
        <button
          type="button"
          onClick={goToCreate}
          className="font-w600 text-brand-700 outline-none transition-colors hover:text-brand-800 focus-visible:underline motion-reduce:transition-none"
          {...testIdProp('auth-get-started')}
        >
          Create an account
        </button>
      </p>
    </AuthShell>
  );
}
